import axios from "axios";

import { resolveApiBase } from "../utils/apiBase";

/**
 * Getting a new access token when the old one runs out.
 *
 * Access tokens last fifteen minutes. The refresh token that buys the next one
 * lives in an httpOnly cookie, which this code cannot read and does not need to:
 * the browser attaches it to the refresh call by itself. That is the point -
 * nothing here ever holds the credential that matters.
 *
 * WHY ONE REFRESH AT A TIME, ALWAYS
 *
 * A page typically has several requests in flight, so when a token expires they
 * all fail at once. Refreshing per failure would present the same refresh token
 * several times - and the server treats a second use of one refresh token as
 * evidence of theft and ends the whole session. Firing them in parallel would
 * therefore sign the user out for the crime of loading two panels at once.
 *
 * So every caller that needs a refresh waits on the same promise. One request
 * goes out, everybody gets its answer.
 */

export const SHOP_TOKEN_KEY = "token";
export const BUSINESS_SESSION_KEY = "business_session";
export const SUPPLIER_SESSION_KEY = "supplier_session";

const REFRESH_PATH = "/api/auth/refresh";

// The refresh call deliberately uses a bare axios instance. Going through the
// application's own client would re-enter these interceptors, and a refresh that
// 401s would try to refresh itself.
export const refreshClient = axios.create({
  baseURL: resolveApiBase() || undefined,
  withCredentials: true,
  timeout: 15000,
});

/** In-flight refresh per portal, so concurrent failures share one call. */
const inFlight = new Map();

function readSession(scope) {
  try {
    if (scope === "business" || scope === "supplier") {
      const raw = localStorage.getItem(scope === "business" ? BUSINESS_SESSION_KEY : SUPPLIER_SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    }
    return localStorage.getItem(SHOP_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

function writeAccessToken(scope, token, onlyIfToken) {
  try {
    // The same guard as clearing: never write into a session that replaced the
    // one this refresh was for.
    if (onlyIfToken !== undefined && currentAccessToken(scope) !== onlyIfToken) return;
    if (scope === "business" || scope === "supplier") {
      const key = scope === "business" ? BUSINESS_SESSION_KEY : SUPPLIER_SESSION_KEY;
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // Only refresh a session that is still there, so an answer arriving after
      // a sign-out cannot bring back the session the person deliberately left.
      if (!parsed?.token) return;
      localStorage.setItem(key, JSON.stringify({ ...parsed, token }));
      return;
    }
    if (!localStorage.getItem(SHOP_TOKEN_KEY)) return;
    localStorage.setItem(SHOP_TOKEN_KEY, token);
  } catch {
    // Storage can be unavailable. The caller still has the token it was handed.
  }
}

/** The access token this portal is currently holding, whatever shape it is in. */
function currentAccessToken(scope) {
  const session = readSession(scope);
  return scope === "business" || scope === "supplier" ? session?.token || null : session;
}

/**
 * End a session that could not be refreshed - unless it is no longer the
 * session we were refreshing.
 *
 * Someone may have signed in again while the refresh was in flight. Clearing
 * blindly would sign them straight back out of a session that is perfectly
 * healthy, because a request belonging to the session they just replaced
 * happened to fail.
 */
export function clearSession(scope, onlyIfToken = undefined) {
  try {
    if (onlyIfToken !== undefined && currentAccessToken(scope) !== onlyIfToken) return;
    const key = scope === "business" ? BUSINESS_SESSION_KEY : scope === "supplier" ? SUPPLIER_SESSION_KEY : SHOP_TOKEN_KEY;
    localStorage.removeItem(key);
    window.dispatchEvent(new StorageEvent("storage", {
      key,
    }));
  } catch {
    // Nothing to clear is the same outcome as having cleared it.
  }
}

/**
 * A fresh access token for this portal, or null if the session is over.
 *
 * Never throws: the caller is already handling a failed request, and a refusal
 * to refresh is an answer, not a second failure to handle.
 */
export function refreshAccessToken(scope = "shop", { failedToken } = {}) {
  const portal = scope === "business" || scope === "supplier" ? scope : "shop";
  const stored = currentAccessToken(portal);

  // No session to refresh. Asking would spend a request to be told so.
  if (!stored) return Promise.resolve(null);

  // The request that failed was using a token this portal has already moved on
  // from - someone signed in again while it was in flight. Its failure says
  // nothing about the session now in place, so refreshing would be answering a
  // question nobody asked, and retrying it would put a stale request's verdict
  // onto a healthy session. Let it fail on its own and leave the session alone.
  if (failedToken !== undefined && failedToken !== null && failedToken !== stored) {
    return Promise.resolve(null);
  }

  if (inFlight.has(portal)) return inFlight.get(portal);
  const refreshingToken = stored;

  const attempt = refreshClient
    .post(REFRESH_PATH, null, { headers: { "X-Portal": portal } })
    .then((response) => {
      const token = response?.data?.token;
      if (!token) return null;
      writeAccessToken(portal, token, refreshingToken);
      return token;
    })
    .catch(() => {
      // The refresh token is gone, expired, or was used twice and the session
      // was ended. All three mean the same thing to the person: sign in again.
      clearSession(portal, refreshingToken);
      return null;
    })
    .finally(() => {
      inFlight.delete(portal);
    });

  inFlight.set(portal, attempt);
  return attempt;
}

/** Exported for tests: forget any in-flight refresh. */
export function resetRefreshState() {
  inFlight.clear();
}
